'use client';

import * as React from 'react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

type Tone = 'muted' | 'foreground' | 'primary' | 'destructive';
type Size = 'xs' | 'sm' | 'md' | 'lg';
type Align = 'center' | 'start';

export type IconLabelProps = {
  icon?: LucideIcon;
  children: React.ReactNode;
  tone?: Tone;
  size?: Size;
  /**
   * How the icon aligns with the text:
   *   center - single-line labels and chips (default). Uses items-center.
   *   start  - multi-line values where the icon should sit with the first
   *            line's cap-height (uses items-start + small icon top-margin).
   */
  align?: Align;
  /** Bold the text (for emphasized labels). */
  strong?: boolean;
  /**
   * Allow the text to wrap. Default false (truncates on overflow). Set true
   * for multi-line data values (e.g., long URLs or addresses).
   */
  wrap?: boolean;
  /** Use break-all instead of break-words (for long URLs). Implies wrap. */
  breakAll?: boolean;
  className?: string;
  /** Extra classes applied to the icon (e.g., `animate-spin` for loaders). */
  iconClassName?: string;
  title?: string;
};

const TONE_CLASS: Record<Tone, string> = {
  muted: 'text-muted-foreground',
  foreground: 'text-foreground',
  primary: 'text-primary',
  destructive: 'text-destructive',
};

const TEXT_SIZE_CLASS: Record<Size, string> = {
  xs: 'text-[10px] font-medium leading-none tracking-wide', // 10px
  sm: 'text-caption',        // 11px
  md: 'text-body-sm',        // 12px
  lg: 'text-body',           // 13px
};

const STRONG_CLASS: Record<Size, string> = {
  xs: 'text-[10px] font-semibold leading-none tracking-wide',
  sm: 'text-label',
  md: 'text-body-sm font-semibold',
  lg: 'text-body font-semibold',
};

const ICON_SIZE_CLASS: Record<Size, string> = {
  xs: 'h-2.5 w-2.5',
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
  lg: 'h-3.5 w-3.5',
};

/**
 * Icon-top nudge to align a small icon with the cap-height of the first
 * line of text when using align="start". Slightly different per size so
 * the icon optically centers on the first line instead of being pushed
 * toward the ascender.
 */
const ICON_ALIGN_START_NUDGE: Record<Size, string> = {
  xs: 'mt-px',
  sm: 'mt-0.5',
  md: 'mt-0.5',
  lg: 'mt-[3px]',
};

/**
 * Icon + text pair at a single consistent size and alignment. Use wherever
 * you'd otherwise write `<Icon className="..." /> + text`.
 */
export function IconLabel({
  icon: Icon,
  children,
  tone = 'muted',
  size = 'sm',
  align = 'center',
  strong = false,
  wrap = false,
  breakAll = false,
  className,
  iconClassName,
  title,
}: IconLabelProps) {
  const shouldWrap = wrap || breakAll;
  return (
    <span
      title={title}
      className={cn(
        shouldWrap ? 'flex' : 'inline-flex',
        align === 'start' ? 'items-start' : 'items-center',
        'gap-1.5',
        shouldWrap ? null : 'whitespace-nowrap',
        strong ? STRONG_CLASS[size] : TEXT_SIZE_CLASS[size],
        TONE_CLASS[tone],
        className,
      )}
    >
      {Icon ? (
        <Icon
          className={cn(
            ICON_SIZE_CLASS[size],
            'shrink-0',
            align === 'start' ? ICON_ALIGN_START_NUDGE[size] : null,
            iconClassName,
          )}
          aria-hidden="true"
        />
      ) : null}
      <span
        className={cn(
          shouldWrap
            ? breakAll
              ? 'min-w-0 break-all'
              : 'min-w-0 break-words'
            : 'min-w-0 truncate',
        )}
      >
        {children}
      </span>
    </span>
  );
}

export type MetaRowProps = {
  children: React.ReactNode;
  /** Bullet character between items. Set to null to remove separators. */
  separator?: React.ReactNode | null;
  size?: Size;
  className?: string;
};

/**
 * Horizontal row of IconLabels with bullet separators.
 * Wraps cleanly on narrow viewports.
 */
export function MetaRow({
  children,
  separator = '\u2022',
  size = 'sm',
  className,
}: MetaRowProps) {
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-2 gap-y-1 text-muted-foreground',
        size === 'sm'
          ? 'text-caption'
          : size === 'md'
            ? 'text-body-sm'
            : 'text-body',
        className,
      )}
    >
      {items.map((child, index) => (
        <React.Fragment key={index}>
          {child}
          {separator && index < items.length - 1 && (
            <span aria-hidden="true" className="text-muted-foreground/50">
              {separator}
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}
